import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import ChatWidget from './components/ChatWidget';
import AdminLayout from './components/AdminLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import OAuthCallback from './pages/OAuthCallback';
import Dashboard from './pages/Dashboard';
import Subscriptions from './pages/Subscriptions';
import Plans from './pages/Plans';
import AdminDashboard from './pages/AdminDashboard';
import AdminUserProfiles from './pages/AdminUserProfiles';
import AdminUserProfileDetail from './pages/AdminUserProfileDetail';
import AdminProcurementRequests from './pages/AdminProcurementRequests';
import AdminProductInventory from './pages/AdminProductInventory';
import AdminTrendBriefing from './pages/AdminTrendBriefing';
import ApplicantProfile from './pages/ApplicantProfile';
import ProfileDetails from './pages/ProfileDetails';
import AddressSettings from './pages/AddressSettings';
import PaymentCardSettings from './pages/PaymentCardSettings';
import HelpCenter from './pages/HelpCenter';
import RefundClaims from './pages/RefundClaims';
import Transactions from './pages/Transactions';
import Home from './pages/Home';
import OrderPortal from './pages/OrderPortal';
import Orders from './pages/Orders';
import PartnerProgram from './pages/partner/PartnerProgram';
import QuickQuote from './pages/QuickQuote';
import Rewards from './pages/Rewards';
import Membership from './pages/Membership';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ShippingPolicy from './pages/ShippingPolicy';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated || !user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <AdminLayout>{children}</AdminLayout>;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/callback/google" element={<OAuthCallback />} />
          <Route path="/auth/callback/github" element={<OAuthCallback />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute>
                <Subscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/plans"
            element={
              <ProtectedRoute>
                <Plans />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfileDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/addresses"
            element={
              <ProtectedRoute>
                <AddressSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/payment-cards"
            element={
              <ProtectedRoute>
                <PaymentCardSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/partner-program"
            element={
              <ProtectedRoute>
                <ApplicantProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/claims"
            element={
              <ProtectedRoute>
                <RefundClaims />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <Transactions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUserProfiles />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <AdminRoute>
                <AdminUserProfileDetail />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/refunds"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/procurement"
            element={
              <AdminRoute>
                <AdminProcurementRequests />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <AdminRoute>
                <AdminProductInventory />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/trend-briefing"
            element={
              <AdminRoute>
                <AdminTrendBriefing />
              </AdminRoute>
            }
          />
          <Route
            path="/order"
            element={
              <ProtectedRoute>
                <OrderPortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <Orders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/help"
            element={
              <ProtectedRoute>
                <HelpCenter />
              </ProtectedRoute>
            }
          />
          <Route path="/partner-program" element={<PartnerProgram />} />
          <Route path="/" element={<Home />} />
          <Route path="/quote" element={<QuickQuote />} />
          <Route path="/rewards" element={
            <ProtectedRoute>
              <Rewards />
            </ProtectedRoute>
          } />
          <Route path="/membership" element={<Membership />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/members/login" element={<Navigate to="/login" replace />} />
          <Route path="/member" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <ChatWidget />
      </AuthProvider>
    </Router>
  );
}

export default App;
